import { Component, OnInit, ViewChild } from '@angular/core';
import { RegistrationOfEnergyProducerRegulatoryData } from '../entities/regep-regulatory-data';
import { FormGroup, NgForm } from '@angular/forms';
import { DestroyableComponent } from 'src/app/common/destroyable.component';
import { map, switchMap } from 'rxjs/operators';
import { RegepApplicationService } from '../services/regep-application.service';
import { ErrorService } from 'src/app/services/error/error.service';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { EditabilityBasedOnProcessStatus } from 'src/app/+grid-application-shared/entities/editabilityBasedOnProcessStatus';
import { RegepProcessStatus } from '../entities/enums/regepProcessStatus';
import { ActivatedRoute, Router } from '@angular/router';
import { Document } from 'src/app/jurisdiction/jurisdiction.domain';
import { RegistrationOfEnergyProducerApplicationDocumentTypes } from '../entities/enums/regepDocumentTypes';
import { RegepOrderProcessService } from '../services/regep-order-process.service';
import { forkJoin, of } from 'rxjs';
import { ComponentCanDeactivate } from 'src/app/+shared/guards/pending-changes-guard.servie';
import { RegistrationOfEnergyProducerTechnicalData } from '../entities/regep-technical-data';
import { Location } from '@angular/common';
import { EegEnergyProducer } from '../entities/regep-eeg-energy-producer';

@Component({
  selector: 'app-regulatory-data-form',
  templateUrl: './regulatory-data-form.component.html',
  styleUrls: ['./regulatory-data-form.component.scss'],
})
export class RegulatoryDataFormComponent extends DestroyableComponent implements OnInit, ComponentCanDeactivate {
  @ViewChild('regulatoryForm', { read: NgForm }) regulatoryForm: NgForm;
  regulatoryData: RegistrationOfEnergyProducerRegulatoryData = new RegistrationOfEnergyProducerRegulatoryData();

  step = 2;
  isLoading: boolean = true;
  isDetailsView: boolean = false;
  isFeedbackChanged: boolean = false;
  historyLogForm: FormGroup;
  applicationId: number;
  externalApplicationId: string;
  techDataEnergySource: number;
  energyProducersFormValid: boolean = true;
  energyProducersFormDirty: boolean;

  editabilityBasedOnProcessStatus: EditabilityBasedOnProcessStatus = new EditabilityBasedOnProcessStatus();

  private role: string = this.localStorageService.getItem('role');
  private preventCanDeactivateCheck: boolean = false;
  private regulatoryDataExists: boolean = false;

  constructor(
    private readonly applicationService: RegepApplicationService,
    private readonly orderProcessService: RegepOrderProcessService,
    private readonly errorService: ErrorService,
    private readonly localStorageService: LocalStorageService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly location: Location
  ) {
    super();
  }

  canDeactivate(): boolean | Promise<boolean> {
    return (
      this.preventCanDeactivateCheck ||
      (!this.regulatoryForm?.dirty &&
        !this.energyProducersFormDirty &&
        !this.isFeedbackChanged &&
        !this.historyLogForm?.dirty &&
        !this.isFeedbackChanged)
    );
  }

  ngOnInit(): void {
    this.isLoading = true;

    this.applicationId = Number(this.route.snapshot.paramMap.get('id'));

    if (this.applicationId) {
      this.loadApplicationData();
    } else {
      this.regulatoryData.application_id = Number(JSON.parse(localStorage.getItem('application_id')));
      this.isLoading = false;
    }
  }

  eegEnergyProducersFormChanged(eegEnergyProducersForm: FormGroup) {
    this.energyProducersFormValid = !!eegEnergyProducersForm?.valid;
    this.energyProducersFormDirty = !!eegEnergyProducersForm?.dirty;
  }

  public get isRegulatoryFormValid(): boolean {
    if (!this.regulatoryForm) return true;

    return this.regulatoryForm.valid || this.regulatoryForm.disabled;
  }

  private loadApplicationData() {
    this.loadOrderProcess();

    this.preventLeak(this.applicationService.getApplication(this.applicationId)).subscribe(
      application => {
        this.externalApplicationId = application.application_ext_id;
        this.prepareDetails(
          application.regulatory_data,
          application.technical_data,
          application.application_id,
          application.eeg_energy_producers
        );
        this.regulatoryData.regulatory_data_sections_state = application.regulatory_data_sections_state;

        this.prepareDocumentsForDetails(application.document);
        application.process_status = this.editabilityBasedOnProcessStatus;

        this.isDetailsView = true;
        this.isLoading = false;
      },
      error => {
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  private loadOrderProcess(): void {
    this.preventLeak(this.orderProcessService.orderProcess$).subscribe(orderProcess => {
      this.setEditabilityBasedOnProcessStatus(orderProcess.status);
    });

    this.orderProcessService.loadOrderProcess(this.applicationId);
  }

  private prepareDetails(
    regulatoryData: RegistrationOfEnergyProducerRegulatoryData,
    technicalData: RegistrationOfEnergyProducerTechnicalData,
    applicationId: string,
    eeg_energy_producers: EegEnergyProducer[]
  ): void {
    this.regulatoryDataExists = !!regulatoryData?.application_id;

    if (!!regulatoryData?.application_id) {
      this.regulatoryDataExists = true;
      this.regulatoryData = regulatoryData;

      this.regulatoryData.is_waiver_compensation = regulatoryData.is_waiver_compensation
        ? JSON.parse(String(regulatoryData.is_waiver_compensation))
        : null;
      this.regulatoryData.is_payment_with_vat = regulatoryData.is_payment_with_vat
        ? JSON.parse(String(regulatoryData.is_payment_with_vat))
        : null;
      this.regulatoryData.is_bafa_auth_granted = regulatoryData.is_bafa_auth_granted
        ? JSON.parse(String(regulatoryData.is_bafa_auth_granted))
        : null;

      this.regulatoryData.compensation_model = regulatoryData.compensation_model ? Number(regulatoryData.compensation_model) : null;
      this.regulatoryData.due_invoice = JSON.parse(regulatoryData.due_invoice?.toString() ?? 'false');
      this.regulatoryData.credit_invoice = JSON.parse(regulatoryData.credit_invoice?.toString() ?? 'false');
    } else {
      this.regulatoryData = {
        application_id: applicationId,
        ...new RegistrationOfEnergyProducerRegulatoryData(),
      };
    }

    this.regulatoryData.eeg_energy_producers = eeg_energy_producers;

    this.techDataEnergySource = technicalData.existing_energy_producers?.[0]?.energy_source;
  }

  private prepareDocumentsForDetails(documents: Document[]): void {
    documents = documents?.sort((a, b) => parseInt(b.document_id) - parseInt(a.document_id));

    this.regulatoryData.bafa_auth_notice_document =
      documents.find(d => d.sk_document_type === RegistrationOfEnergyProducerApplicationDocumentTypes.BafaNoticeOfAuthorization) ||
      new Document(RegistrationOfEnergyProducerApplicationDocumentTypes.BafaNoticeOfAuthorization);
  }

  public saveFormData(shouldProceed: boolean) {
    if (this.regulatoryData.bank_iban) {
      this.regulatoryData.bank_iban = this.regulatoryData.bank_iban.replace(/\s+/g, '');
    }

    if (!this.regulatoryDataExists) {
      return this.createForm();
    }

    if (this.isDetailsView) {
      return this.updateForm(shouldProceed);
    }

    return this.createForm();
  }

  public previousPage(): void {
    this.location.back();
  }

  private createForm() {
    this.isLoading = true;

    this.preventLeak(this.applicationService.createRegulatoryData(this.regulatoryData))
      .pipe(
        map(() =>
          this.prepareDocumentsForUpload().map(document =>
            this.applicationService.uploadDocuments([document], this.applicationId.toString())
          )
        ),
        switchMap(uploads$ => forkJoin([...uploads$, of(1)]))
      )
      .subscribe(
        () => {
          this.isLoading = false;
          this.proceed();
        },
        error => this.errorService.throwErrorIfFailure(error, true)
      );
  }

  private updateForm(shouldProceed: boolean) {
    if (this.role === 'observer' && shouldProceed) {
      return this.proceed();
    }

    this.isLoading = true;

    this.preventLeak(this.applicationService.updateRegulatoryData(this.regulatoryData))
      .pipe(
        map(() =>
          this.prepareDocumentsForUpload().map(document =>
            this.applicationService.updateDocuments([document], this.applicationId.toString())
          )
        ),
        switchMap(uploads$ => forkJoin([...uploads$, of(1)]))
      )
      .subscribe(
        () => {
          this.isLoading = false;
          this.isFeedbackChanged = false;
          this.updateLocalStorageForSectionStateAndProcessStatus();

          if (this.applicationId && !shouldProceed) {
            return this.loadApplicationData();
          }

          this.proceed();
        },
        error => this.errorService.throwErrorIfFailure(error, true)
      );
  }

  private prepareDocumentsForUpload(): Document[] {
    let documentsToUpload: Document[] = [];

    if (this.regulatoryData.bafa_auth_notice_document?.file) {
      documentsToUpload.push(this.regulatoryData.bafa_auth_notice_document);
    }

    return documentsToUpload;
  }

  private proceed(): void {
    let redirectUrl = this.isDetailsView
      ? `/${this.route.snapshot.data.module}/personal-data/${this.applicationId}`
      : `/${this.route.snapshot.data.module}/personal-data`;

    this.preventCanDeactivateCheck = true;
    this.router.navigate([redirectUrl]);
  }

  private setEditabilityBasedOnProcessStatus(status: RegepProcessStatus): void {
    const customerRoleEditableStatuses = [RegepProcessStatus.EngineeringOk, RegepProcessStatus.RegulationsOpenIssue];
    const internalRoleEditableStatuses = [RegepProcessStatus.RegulationsRequestUpdated, RegepProcessStatus.CustomerDataAdded];

    this.editabilityBasedOnProcessStatus.technicianRole = false;
    this.editabilityBasedOnProcessStatus.customerRole = customerRoleEditableStatuses.includes(status);
    this.editabilityBasedOnProcessStatus.internalRoles = internalRoleEditableStatuses.includes(status);
  }

  private updateLocalStorageForSectionStateAndProcessStatus(): void {
    this.applicationService.application.regulatory_data_sections_state = this.regulatoryData.regulatory_data_sections_state;
    this.applicationService.application.process_status = this.editabilityBasedOnProcessStatus;
  }
}
